/**
 * GraphQL Service for Web App
 * Similar to iOS GraphQLService - provides data management with caching
 */

import { appSyncClient } from "./appsync-client";

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const SHORT_CACHE_DURATION = 60 * 1000; // 1 minute
const LONG_CACHE_DURATION = 60 * 60 * 1000; // 1 hour

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  duration: number;
}

class GraphQLService {
  private static instance: GraphQLService;
  private cache: Map<string, CacheEntry<any>> = new Map();

  private constructor() {}

  static getInstance(): GraphQLService {
    if (!GraphQLService.instance) {
      GraphQLService.instance = new GraphQLService();
    }
    return GraphQLService.instance;
  }

  /**
   * Get cached data if available and not expired
   */
  private getCached<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const age = Date.now() - entry.timestamp;
    if (age > entry.duration) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * Set cache entry
   */
  private setCache<T>(key: string, data: T, duration: number = CACHE_DURATION): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      duration,
    });
  }

  /**
   * Clear cache for a specific key or all cache
   */
  clearCache(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get today's lessons
   */
  async getTodaysLessons(date: string, useCache: boolean = true): Promise<any[]> {
    const cacheKey = `todaysLessons:${date}`;
    
    if (useCache) {
      const cached = this.getCached<any[]>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const query = `
      query GetTodaysLessons($date: String!) {
        getTodaysLessons(date: $date) {
          id
          tutorId
          studentId
          student {
            id
            firstName
            lastName
          }
          locationId
          location {
            id
            name
            address
          }
          subject
          topic
          time
          duration
          date
          status
          hourlyRate
          notes
          homework
          pieces
          recurring
          parentRecurringLessonId
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ getTodaysLessons: any[] }>(query, { date });
      const lessons = result.data?.getTodaysLessons || [];
      
      if (useCache) {
        this.setCache(cacheKey, lessons, SHORT_CACHE_DURATION);
      }
      
      return lessons;
    } catch (error) {
      console.error("Error fetching today's lessons:", error);
      throw error;
    }
  }

  /**
   * Get lessons needing marking
   */
  async getLessonsNeedingMarking(beforeDate: string, fromDate: string, useCache: boolean = true): Promise<any[]> {
    const cacheKey = `lessonsNeedingMarking:${beforeDate}:${fromDate}`;
    
    if (useCache) {
      const cached = this.getCached<any[]>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const query = `
      query GetLessonsNeedingMarking($beforeDate: String!, $fromDate: String!) {
        getLessonsNeedingMarking(beforeDate: $beforeDate, fromDate: $fromDate) {
          id
          tutorId
          studentId
          student {
            id
            firstName
            lastName
          }
          locationId
          location {
            id
            name
          }
          subject
          time
          duration
          date
          status
          hourlyRate
          notes
          homework
          recurring
          parentRecurringLessonId
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ getLessonsNeedingMarking: any[] }>(query, {
        beforeDate,
        fromDate,
      });
      const lessons = result.data?.getLessonsNeedingMarking || [];
      
      if (useCache) {
        this.setCache(cacheKey, lessons, SHORT_CACHE_DURATION);
      }
      
      return lessons;
    } catch (error) {
      console.error("Error fetching lessons needing marking:", error);
      throw error;
    }
  }

  /**
   * Get calendar events for date range
   */
  async getCalendarEvents(startDate: string, endDate: string, useCache: boolean = true): Promise<any[]> {
    const cacheKey = `calendarEvents:${startDate}:${endDate}`;
    
    if (useCache) {
      const cached = this.getCached<any[]>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const query = `
      query ListCalendarEvents($startDate: String!, $endDate: String!) {
        listCalendarEvents(startDate: $startDate, endDate: $endDate) {
          id
          tutorId
          studentId
          student {
            id
            firstName
            lastName
          }
          familyId
          family {
            id
            familyName
          }
          locationId
          location {
            id
            name
            address
          }
          startTime
          endTime
          date
          status
          hourlyRate
          cancellationPolicy
          notes
          seriesId
          seriesIndex
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ listCalendarEvents: any[] }>(query, {
        startDate,
        endDate,
      });
      const events = result.data?.listCalendarEvents || [];
      
      if (useCache) {
        this.setCache(cacheKey, events, SHORT_CACHE_DURATION);
      }
      
      return events;
    } catch (error) {
      console.error("Error fetching calendar events:", error);
      throw error;
    }
  }

  /**
   * Mark lesson as present
   */
  async markLessonPresent(lessonId: string): Promise<any> {
    const mutation = `
      mutation MarkLessonPresent($id: ID!) {
        markLessonPresent(id: $id) {
          id
          tutorId
          studentId
          status
          date
          time
          lessonFee
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ markLessonPresent: any }>(mutation, {
        id: lessonId,
      });
      
      // Clear relevant caches
      this.clearCache();
      
      return result.data?.markLessonPresent;
    } catch (error) {
      console.error("Error marking lesson as present:", error);
      throw error;
    }
  }

  /**
   * Mark lesson as cancelled
   */
  async markLessonCancelled(lessonId: string): Promise<any> {
    const mutation = `
      mutation MarkLessonCancelled($id: ID!) {
        markLessonCancelled(id: $id) {
          id
          tutorId
          studentId
          status
          date
          time
          lessonFee
          cancelledAt
          cancellationReason
          cancellationComment
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ markLessonCancelled: any }>(mutation, {
        id: lessonId,
      });
      
      // Clear relevant caches
      this.clearCache();
      
      return result.data?.markLessonCancelled;
    } catch (error) {
      console.error("Error marking lesson as cancelled:", error);
      throw error;
    }
  }

  /**
   * Update lesson (for cases where we need to update more than just status)
   */
  async updateLesson(input: {
    id: string;
    studentId?: string;
    familyId?: string;
    locationId?: string;
    subject?: string;
    topic?: string;
    time?: string;
    duration?: number;
    date?: string;
    status?: string;
    hourlyRate?: number;
    notes?: string;
    homework?: string;
    pieces?: string[];
    cancellationReason?: string;
    cancellationComment?: string;
    // Cancellation fee tracking
    lessonFee?: number;
    cancelledAt?: string;
    policyImplication?: string;
  }): Promise<any> {
    const mutation = `
      mutation UpdateLesson($input: UpdateLessonInput!) {
        updateLesson(input: $input) {
          id
          tutorId
          studentId
          familyId
          locationId
          subject
          status
          date
          time
          duration
          hourlyRate
          lessonFee
          notes
          homework
          seriesId
          cancellationReason
          cancellationComment
          cancelledAt
          policyImplication
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ updateLesson: any }>(mutation, {
        input,
      });
      
      // Clear relevant caches
      this.clearCache();
      
      return result.data?.updateLesson;
    } catch (error) {
      console.error("Error updating lesson:", error);
      throw error;
    }
  }

  /**
   * Create lesson (for creating recurring lesson instances)
   */
  async createLesson(input: {
    studentId: string;
    familyId?: string;
    locationId?: string;
    subject?: string;
    topic?: string;
    time?: string;
    duration?: number;
    date: string;
    status?: string;
    hourlyRate?: number;
    notes?: string;
    homework?: string;
    pieces?: string[];
    recurring?: boolean;
    parentRecurringLessonId?: string;
    seriesId?: string;
    seriesIndex?: number;
    // Cancellation fields (for creating cancelled lessons directly)
    cancellationReason?: string;
    cancellationComment?: string;
    lessonFee?: number;
    cancelledAt?: string;
    policyImplication?: string;
  }): Promise<any> {
    const mutation = `
      mutation CreateLesson($input: CreateLessonInput!) {
        createLesson(input: $input) {
          id
          tutorId
          studentId
          date
          time
          status
          notes
          homework
          recurring
          parentRecurringLessonId
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ createLesson: any }>(mutation, {
        input,
      });
      
      // Clear relevant caches
      this.clearCache();
      
      return result.data?.createLesson;
    } catch (error) {
      console.error("Error creating lesson:", error);
      throw error;
    }
  }

  /**
   * Create lesson series (for recurring lessons)
   * Matches iOS app implementation
   */
  async createLessonSeries(input: {
    studentId: string;
    familyId?: string;
    locationId?: string;
    subject: string;
    time: string;
    duration: number;
    dayOfWeek: string;
    dayOfWeekNum: number;
    startDate: string;
    endDate?: string;
    customLessonFee?: number;
    notes?: string;
    status: string;
    recurringEndDateEventId?: string; // ID of the term/holiday event for the end date
  }): Promise<any> {
    const mutation = `
      mutation CreateLessonSeries($input: CreateLessonSeriesInput!) {
        createLessonSeries(input: $input) {
          id
          tutorId
          studentId
          familyId
          locationId
          subject
          time
          duration
          dayOfWeek
          dayOfWeekNum
          startDate
          endDate
          customLessonFee
          notes
          status
          recurringEndDateEventId
          createdAt
          updatedAt
        }
      }
    `;

    try {
      console.log('📡 Creating lesson series via GraphQL mutation:', input);
      
      const result = await appSyncClient.execute<{ createLessonSeries: any }>(mutation, {
        input,
      });
      
      console.log('✅ Lesson series created successfully:', result.data?.createLessonSeries?.id);
      
      // Clear relevant caches
      this.clearCache();
      
      return result.data?.createLessonSeries;
    } catch (error) {
      console.error("❌ Error creating lesson series:", error);
      throw error;
    }
  }

  /**
   * Update lesson series (for recurring lessons)
   */
  async updateLessonSeries(input: {
    id: string;
    studentId?: string;
    familyId?: string;
    locationId?: string;
    subject?: string;
    time?: string;
    duration?: number;
    dayOfWeek?: string;
    dayOfWeekNum?: number;
    startDate?: string;
    endDate?: string;
    customLessonFee?: number;
    notes?: string;
    status?: string;
    recurringEndDateEventId?: string; // ID of the term/holiday event for the end date
  }): Promise<any> {
    const mutation = `
      mutation UpdateLessonSeries($input: UpdateLessonSeriesInput!) {
        updateLessonSeries(input: $input) {
          id
          tutorId
          studentId
          familyId
          locationId
          subject
          time
          duration
          dayOfWeek
          dayOfWeekNum
          startDate
          endDate
          recurringEndDateEventId
          customLessonFee
          notes
          status
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ updateLessonSeries: any }>(mutation, {
        input,
      });
      
      // Clear relevant caches
      this.clearCache();
      
      return result.data?.updateLessonSeries;
    } catch (error) {
      console.error("❌ Error updating lesson series:", error);
      throw error;
    }
  }

  /**
   * List active lesson series for a given date
   */
  async listActiveLessonSeries(date: string, useCache: boolean = true): Promise<any[]> {
    const cacheKey = `activeLessonSeries:${date}`;
    
    if (useCache) {
      const cached = this.getCached<any[]>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const query = `
      query ListActiveLessonSeries($date: String!) {
        listActiveLessonSeries(date: $date) {
          id
          tutorId
          studentId
          student {
            id
            firstName
            lastName
          }
          familyId
          family {
            id
            familyName
          }
          locationId
          location {
            id
            name
            address
          }
          subject
          time
          duration
          dayOfWeek
          dayOfWeekNum
          startDate
          endDate
          recurringEndDateEventId
          customLessonFee
          notes
          status
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ listActiveLessonSeries: any[] }>(query, { date });
      const series = result.data?.listActiveLessonSeries || [];
      
      if (useCache) {
        this.setCache(cacheKey, series, SHORT_CACHE_DURATION);
      }
      
      return series;
    } catch (error) {
      console.error("Error fetching active lesson series:", error);
      throw error;
    }
  }

  /**
   * List students
   */
  async listStudents(limit?: number, useCache: boolean = true): Promise<any[]> {
    const cacheKey = `students:${limit || 'all'}`;
    
    if (useCache) {
      const cached = this.getCached<any[]>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const query = `
      query ListStudents($limit: Int) {
        listStudents(limit: $limit) {
          items {
            id
            tutorId
            firstName
            lastName
            email
            phone
            dateOfBirth
            emergencyContact
            emergencyPhone
            notes
            subject
            grade
            classGroupId
            yearLevel
            schoolLocationId
            plannerMode
            avoidSubjects
            plannerFlexibility
            familyId
            family {
              id
              familyName
            }
            createdAt
            updatedAt
          }
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ listStudents: { items: any[] } }>(query, {
        limit: limit || 1000,
      });
      const students = result.data?.listStudents?.items || [];
      
      if (useCache) {
        this.setCache(cacheKey, students, LONG_CACHE_DURATION);
      }
      
      return students;
    } catch (error) {
      console.error("Error fetching students:", error);
      throw error;
    }
  }

  /**
   * Get student by ID with full details including family
   */
  async getStudent(id: string, useCache: boolean = true): Promise<any | null> {
    const cacheKey = `student:${id}`;

    if (useCache) {
      const cached = this.getCached<any>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const query = `
      query GetStudent($id: ID!) {
        getStudent(id: $id) {
          id
          tutorId
          firstName
          lastName
          email
          phone
          dateOfBirth
          emergencyContact
          emergencyPhone
          notes
          subject
          grade
          classGroupId
          yearLevel
          schoolLocationId
          plannerMode
          avoidSubjects
          plannerFlexibility
          familyId
          family {
            id
            familyName
            address
            phone
            email
            notes
            carers {
              firstName
              lastName
              email
              phone
              relationship
              isPrimary
            }
          }
          familyDetails {
            familyName
            address
            phone
            email
            notes
            carers {
              firstName
              lastName
              email
              phone
              relationship
              isPrimary
            }
          }
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ getStudent: any }>(query, { id });
      const student = result.data?.getStudent;

      if (student && useCache) {
        this.setCache(cacheKey, student, SHORT_CACHE_DURATION);
      }

      return student || null;
    } catch (error) {
      console.error("Error fetching student:", error);
      throw error;
    }
  }

  /**
   * Get family by ID with carers
   */
  async getFamily(id: string, useCache: boolean = true): Promise<any | null> {
    const cacheKey = `family:${id}`;

    if (useCache) {
      const cached = this.getCached<any>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const query = `
      query GetFamily($id: ID!) {
        getFamily(id: $id) {
          id
          tutorId
          familyName
          address
          phone
          email
          notes
          creditBalance
          credits {
            id
            amount
            reason
            type
            invoiceId
            createdAt
            createdBy
          }
          carers {
            firstName
            lastName
            email
            phone
            relationship
            isPrimary
          }
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ getFamily: any }>(query, { id });
      const family = result.data?.getFamily;

      if (family && useCache) {
        this.setCache(cacheKey, family, SHORT_CACHE_DURATION);
      }

      return family || null;
    } catch (error) {
      console.error("Error fetching family:", error);
      throw error;
    }
  }

  /**
   * List locations with availability rules
   */
  async listLocations(limit?: number, useCache: boolean = true): Promise<any[]> {
    const cacheKey = `locations:${limit || 'all'}`;
    
    if (useCache) {
      const cached = this.getCached<any[]>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const query = `
      query ListLocations($limit: Int) {
        listLocations(limit: $limit) {
          items {
            id
            tutorId
            name
            address
            hourlyRate
            cancellationPolicy
            notes
            status
            locationPreset
            availabilityRules {
              dayOfWeek
              startTime
              endTime
              active
            }
            calendars {
              calendarType
              availability_effect
              isActive
              priority
            }
            createdAt
            updatedAt
          }
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ listLocations: { items: any[] } }>(query, {
        limit: limit || 1000,
      });
      const locations = result.data?.listLocations?.items || [];
      
      if (useCache) {
        this.setCache(cacheKey, locations, LONG_CACHE_DURATION);
      }
      
      return locations;
    } catch (error) {
      console.error("Error fetching locations:", error);
      throw error;
    }
  }

  /**
   * Get lesson by ID
   */
  async getLesson(id: string, useCache: boolean = true): Promise<any | null> {
    const cacheKey = `lesson:${id}`;
    
    if (useCache) {
      const cached = this.getCached<any>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const query = `
      query GetLesson($id: ID!) {
        getLesson(id: $id) {
          id
          tutorId
          studentId
          student {
            id
            firstName
            lastName
          }
          locationId
          location {
            id
            name
          }
          subject
          time
          duration
          date
          status
          hourlyRate
          notes
          homework
          recurring
          parentRecurringLessonId
          recurringEndDateEventId
          seriesId
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ getLesson: any }>(query, { id });
      const lesson = result.data?.getLesson;
      
      if (lesson && useCache) {
        this.setCache(cacheKey, lesson, SHORT_CACHE_DURATION);
      }
      
      return lesson || null;
    } catch (error) {
      console.error("Error fetching lesson:", error);
      throw error;
    }
  }
  
  /**
   * Get lesson series by ID (includes all series-specific fields)
   */
  async getLessonSeries(id: string, useCache: boolean = true): Promise<any | null> {
    const cacheKey = `lessonSeries:${id}`;
    
    if (useCache) {
      const cached = this.getCached<any>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const query = `
      query GetLessonSeries($id: ID!) {
        getLessonSeries(id: $id) {
          id
          tutorId
          studentId
          student {
            id
            firstName
            lastName
          }
          familyId
          locationId
          location {
            id
            name
          }
          subject
          time
          duration
          dayOfWeek
          dayOfWeekNum
          startDate
          endDate
          recurringEndDateEventId
          customLessonFee
          notes
          status
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ getLessonSeries: any }>(query, { id });
      const series = result.data?.getLessonSeries;
      
      if (series && useCache) {
        this.setCache(cacheKey, series, SHORT_CACHE_DURATION);
      }
      
      return series || null;
    } catch (error) {
      console.error("Error fetching lesson series:", error);
      throw error;
    }
  }

  /**
   * List lessons with filters
   */
  async listLessons(params: {
    limit?: number;
    studentId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  } = {}, useCache: boolean = true): Promise<any[]> {
    const cacheKey = `lessons:${JSON.stringify(params)}`;
    
    if (useCache) {
      const cached = this.getCached<any[]>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const query = `
      query ListLessons(
        $limit: Int
        $nextToken: String
        $studentId: ID
        $status: String
        $startDate: String
        $endDate: String
      ) {
        listLessons(
          limit: $limit
          nextToken: $nextToken
          studentId: $studentId
          status: $status
          startDate: $startDate
          endDate: $endDate
        ) {
          items {
            id
            tutorId
            studentId
            student {
              id
              firstName
              lastName
            }
            locationId
            location {
              id
              name
            }
            subject
            time
            duration
            date
            status
            hourlyRate
            lessonFee
            notes
            homework
            recurring
            recurringEndDateEventId
            parentRecurringLessonId
            seriesId
            cancellationReason
            cancellationComment
            createdAt
            updatedAt
          }
          nextToken
        }
      }
    `;

    try {
      const allLessons: any[] = [];
      let nextToken: string | undefined;
      do {
        const result = await appSyncClient.execute<{ listLessons: { items: any[]; nextToken?: string | null } }>(query, {
          ...params,
          limit: params.limit || 1000,
          nextToken,
        });
        allLessons.push(...(result.data?.listLessons?.items || []));
        nextToken = result.data?.listLessons?.nextToken || undefined;
      } while (nextToken && allLessons.length < 5000);
      
      if (useCache) {
        this.setCache(cacheKey, allLessons, SHORT_CACHE_DURATION);
      }
      
      return allLessons;
    } catch (error) {
      console.error("Error fetching lessons:", error);
      throw error;
    }
  }
  // ============================================
  // Profile
  // ============================================

  async getProfile(useCache: boolean = true): Promise<any> {
    const cacheKey = "profile";

    if (useCache) {
      const cached = this.getCached<any>(cacheKey);
      if (cached) return cached;
    }

    const query = `
      query GetProfile {
        getProfile {
          id
          email
          firstName
          lastName
          businessAccount {
            businessName
            address
            hourlyRate
            paymentDetails {
              bankAccount {
                accountName
                bsb
                accountNumber
              }
            }
            businessRegistration {
              abn
            }
            gstRegistration {
              isRegistered
            }
          }
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ getProfile: any }>(query);
      const profile = result.data?.getProfile || null;
      if (profile && useCache) {
        this.setCache(cacheKey, profile, LONG_CACHE_DURATION);
      }
      return profile;
    } catch (error) {
      console.error("Error fetching profile:", error);
      throw error;
    }
  }

  // ============================================
  // Gig Customers
  // ============================================

  async listGigCustomers(useCache: boolean = true): Promise<any[]> {
    const cacheKey = 'gigCustomers';
    
    if (useCache) {
      const cached = this.getCached<any[]>(cacheKey);
      if (cached) return cached;
    }

    const query = `
      query ListGigCustomers {
        listGigCustomers(limit: 500) {
          items {
            id
            tutorId
            customerType
            name
            contactName
            email
            phone
            address
            abn
            notes
            status
            createdAt
            updatedAt
          }
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ listGigCustomers: { items: any[] } }>(query);
      const customers = result.data?.listGigCustomers?.items || [];
      if (useCache) this.setCache(cacheKey, customers, SHORT_CACHE_DURATION);
      return customers;
    } catch (error) {
      console.error("Error fetching gig customers:", error);
      throw error;
    }
  }

  async getGigCustomer(id: string): Promise<any> {
    const query = `
      query GetGigCustomer($id: ID!) {
        getGigCustomer(id: $id) {
          id
          tutorId
          customerType
          name
          contactName
          email
          phone
          address
          abn
          notes
          status
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ getGigCustomer: any }>(query, { id });
      return result.data?.getGigCustomer || null;
    } catch (error) {
      console.error("Error fetching gig customer:", error);
      throw error;
    }
  }

  async createGigCustomer(input: {
    customerType: string;
    name: string;
    contactName?: string;
    email?: string;
    phone?: string;
    address?: string;
    abn?: string;
    notes?: string;
    status?: string;
  }): Promise<any> {
    const mutation = `
      mutation CreateGigCustomer($input: CreateGigCustomerInput!) {
        createGigCustomer(input: $input) {
          id
          tutorId
          customerType
          name
          contactName
          email
          phone
          address
          abn
          notes
          status
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ createGigCustomer: any }>(mutation, { input });
      this.clearCache('gigCustomers');
      return result.data?.createGigCustomer;
    } catch (error) {
      console.error("Error creating gig customer:", error);
      throw error;
    }
  }

  async updateGigCustomer(input: {
    id: string;
    customerType?: string;
    name?: string;
    contactName?: string;
    email?: string;
    phone?: string;
    address?: string;
    abn?: string;
    notes?: string;
    status?: string;
  }): Promise<any> {
    const mutation = `
      mutation UpdateGigCustomer($input: UpdateGigCustomerInput!) {
        updateGigCustomer(input: $input) {
          id
          tutorId
          customerType
          name
          contactName
          email
          phone
          address
          abn
          notes
          status
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ updateGigCustomer: any }>(mutation, { input });
      this.clearCache('gigCustomers');
      return result.data?.updateGigCustomer;
    } catch (error) {
      console.error("Error updating gig customer:", error);
      throw error;
    }
  }

  async deleteGigCustomer(id: string): Promise<boolean> {
    const mutation = `
      mutation DeleteGigCustomer($id: ID!) {
        deleteGigCustomer(id: $id)
      }
    `;

    try {
      await appSyncClient.execute(mutation, { id });
      this.clearCache('gigCustomers');
      return true;
    } catch (error) {
      console.error("Error deleting gig customer:", error);
      throw error;
    }
  }

  // ============================================
  // Gig Invoices
  // ============================================

  async listGigInvoices(customerId?: string, useCache: boolean = true): Promise<any[]> {
    const cacheKey = `gigInvoices:${customerId || 'all'}`;
    
    if (useCache) {
      const cached = this.getCached<any[]>(cacheKey);
      if (cached) return cached;
    }

    const query = `
      query ListGigInvoices($customerId: ID) {
        listGigInvoices(limit: 500, customerId: $customerId) {
          items {
            id
            tutorId
            customerId
            customer {
              id
              name
              customerType
              email
            }
            gigName
            invoiceNumber
            status
            items {
              description
              date
              rate
              quantity
              amount
            }
            subtotal
            gstAmount
            totalAmount
            issueDate
            dueDate
            paidDate
            notes
            receiptNumber
            receiptGeneratedAt
            s3ReceiptPdfPath
            receiptSentAt
            current
            originalInvoiceId
            previousVersionId
            previousVersionNumber
            supersededBy
            supersededByNumber
            voidedAt
            voidReason
            s3PdfPath
            deliveryInfo {
              deliveryMethod
              deliveryMode
              sentAt
              sentTo
            }
            createdAt
            updatedAt
          }
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ listGigInvoices: { items: any[] } }>(query, { customerId });
      const invoices = result.data?.listGigInvoices?.items || [];
      if (useCache) this.setCache(cacheKey, invoices, SHORT_CACHE_DURATION);
      return invoices;
    } catch (error) {
      console.error("Error fetching gig invoices:", error);
      throw error;
    }
  }

  async getGigInvoice(id: string): Promise<any> {
    const query = `
      query GetGigInvoice($id: ID!) {
        getGigInvoice(id: $id) {
          id
          tutorId
          customerId
          customer {
            id
            name
            customerType
            contactName
            email
            phone
            address
            abn
          }
          gigName
          invoiceNumber
          status
          items {
            description
            date
            rate
            quantity
            amount
          }
          subtotal
          gstAmount
          totalAmount
          issueDate
          dueDate
          paidDate
          notes
          receiptNumber
          receiptGeneratedAt
          s3ReceiptPdfPath
          receiptSentAt
          current
          originalInvoiceId
          previousVersionId
          previousVersionNumber
          supersededBy
          supersededByNumber
          voidedAt
          voidReason
          s3PdfPath
          deliveryInfo {
            deliveryMethod
            deliveryMode
            sentAt
            sentTo
          }
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ getGigInvoice: any }>(query, { id });
      return result.data?.getGigInvoice || null;
    } catch (error) {
      console.error("Error fetching gig invoice:", error);
      throw error;
    }
  }

  async createGigInvoice(input: {
    customerId: string;
    gigName?: string;
    invoiceNumber?: string;
    items: Array<{
      description: string;
      date?: string;
      rate: number;
      quantity: number;
      amount: number;
    }>;
    subtotal: number;
    gstAmount?: number;
    totalAmount: number;
    issueDate: string;
    dueDate: string;
    notes?: string;
    status?: string;
  }): Promise<any> {
    const mutation = `
      mutation CreateGigInvoice($input: CreateGigInvoiceInput!) {
        createGigInvoice(input: $input) {
          id
          tutorId
          customerId
          gigName
          invoiceNumber
          status
          items {
            description
            date
            rate
            quantity
            amount
          }
          subtotal
          gstAmount
          totalAmount
          issueDate
          dueDate
          notes
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ createGigInvoice: any }>(mutation, { input });
      this.clearCache();
      return result.data?.createGigInvoice;
    } catch (error) {
      console.error("Error creating gig invoice:", error);
      throw error;
    }
  }

  async updateGigInvoice(input: {
    id: string;
    customerId?: string;
    gigName?: string;
    invoiceNumber?: string;
    items?: Array<{
      description: string;
      date?: string;
      rate: number;
      quantity: number;
      amount: number;
    }>;
    subtotal?: number;
    gstAmount?: number;
    totalAmount?: number;
    issueDate?: string;
    dueDate?: string;
    paidDate?: string;
    notes?: string;
    status?: string;
  }): Promise<any> {
    const mutation = `
      mutation UpdateGigInvoice($input: UpdateGigInvoiceInput!) {
        updateGigInvoice(input: $input) {
          id
          tutorId
          customerId
          gigName
          invoiceNumber
          status
          items {
            description
            date
            rate
            quantity
            amount
          }
          subtotal
          gstAmount
          totalAmount
          issueDate
          dueDate
          paidDate
          notes
          receiptNumber
          receiptGeneratedAt
          s3ReceiptPdfPath
          receiptSentAt
          current
          originalInvoiceId
          previousVersionId
          previousVersionNumber
          supersededBy
          supersededByNumber
          voidedAt
          voidReason
          s3PdfPath
          deliveryInfo {
            deliveryMethod
            deliveryMode
            sentAt
            sentTo
          }
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const result = await appSyncClient.execute<{ updateGigInvoice: any }>(mutation, { input });
      this.clearCache();
      return result.data?.updateGigInvoice;
    } catch (error) {
      console.error("Error updating gig invoice:", error);
      throw error;
    }
  }

  async deleteGigInvoice(id: string): Promise<boolean> {
    const mutation = `
      mutation DeleteGigInvoice($id: ID!) {
        deleteGigInvoice(id: $id)
      }
    `;

    try {
      await appSyncClient.execute(mutation, { id });
      this.clearCache();
      return true;
    } catch (error) {
      console.error("Error deleting gig invoice:", error);
      throw error;
    }
  }
}

export const graphQLService = GraphQLService.getInstance();

